import { useActions } from 'components/shared/hooks/useActions';
import { useAuthUser } from '../../../../components/shared/hooks/useAuthUser';
import { useCallback, useEffect } from 'react';
import { useStateIfMounted } from 'use-state-if-mounted';
import actionCreators from 'actionCreators';

/**
 * @typedef {Object} UseManageTwilioAuthTokenHookValue - the return value of useManageTwilioAuthTokenHook
 * @property {string | null} token - the refresh token needed to sign Twilio API calls from the client
 * @property {function} refreshToken - a function to refresh the signed Twilio API token
 * @property {string | null} error - if an error occurs in retrieving the token or connecting to Twilio, this will be set
 */

/**
 * A hook that manages the Twilio API token needed to sign requests to
 * the Twilio API made from the client.
 * @param {string | undefined} impersonatedUserId  - Optional. The user's ID to dependency-inject for administrative esthetician impersonation.
 * @returns {UseManageTwilioAuthTokenHookValue}
 */
export const useManageTwilioAuthToken = (impersonatedUserId = null) => {
  const { refreshTwilioToken } = useActions(actionCreators.authUserActions);
  const authUser = useAuthUser(impersonatedUserId);
  const userId = impersonatedUserId ?? authUser?.id;

  const [refreshingToken, setRefreshingToken] = useStateIfMounted(false);
  const [token, setToken] = useStateIfMounted(null);
  const [error, setError] = useStateIfMounted(null);

  const refreshToken = useCallback(async () => {
    /** Don't make unnecessary calls if we're already fetching token */
    if (refreshingToken) return;

    try {
      setRefreshingToken(true);
      const response = await refreshTwilioToken(userId);
      return response?.value?.data?.refreshTwilioToken;
    } catch (error) {
      setError('An unexpected error occurred.');
    } finally {
      setRefreshingToken(false);
    }
  }, [userId, refreshTwilioToken]);

  /**
   * Issue mutation to refresh the Twilio API token
   * once we have a user, or if the token has been
   * removed.
   */
  useEffect(() => {
    const handleTokenRefresh = async () => {
      const refreshedToken = await refreshToken();
      if (refreshedToken) setToken(refreshedToken);
    };

    /** If we don't have the information we need to get a token or already have one, don't bother getting a token. */
    if (!userId || token) return;
    handleTokenRefresh();
  }, [userId, token]);

  /**
   * Reissue mutation to refresh the Twilio API token
   * if an impersonatedUserId is dependency injected.
   */
  useEffect(() => {
    setToken(null);
  }, [impersonatedUserId]);

  /** When the user logs out, clear their Twilio token */
  useEffect(() => {
    if (!userId) setToken(null);
  }, [userId]);

  return {
    token,
    refreshToken,
    error
  };
};
